import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable
} from 'react-beautiful-dnd';
import React, { useState } from 'react';

import { AddColumnElement } from './AddColumnElement';
import { AddRowElement } from './AddRowElement';
import ColorPickerService from '../../../../shared/services/ColorPickerService';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Grid from '@mui/material/Unstable_Grid2';
import { IconButton } from '@mui/material';
import { IconButtonWithConfirmation } from '../../../../shared/components/IconButtonWithConfirmation';
import { InlineQuillEdit } from '../../InlineQuillEdit';
import LessonComponentService from '../../../services/LessonComponentService';
import { LessonComponentStyleType } from '../../../models/LessonComponentStyleType';
import NewComponentService from '../../../services/NewComponentService';
import RemoveCircleOutlinedIcon from '@mui/icons-material/RemoveCircleOutlined';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import { TableComponent } from '../../../models/LessonComponent';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { TableRowItem } from '../../../models/LessonComponentContent';
import { browser } from '../../../services/Utilities';
import { cloneDeep } from 'lodash';
import { getColorString } from '../../../services/StyleService';
import { nanoid } from 'nanoid';
import styles from '../../../styles/TableComponent.module.css';
import { useLessonEditContext } from '../../../models/LessonEditContext';
import { useTheme } from '@mui/material/styles';

/** Table component cell height to use 100%.
 * <TableCell>'s child div height is not 100% for browsers other than 'FireFox'.
 * In browsers like Chrome, Edge, Safari the child div height is content's height.
 * But in FireFox it is '100%' of it's parent. In order to overcome the problem
 * identify the browser and apply height.
 */
const cellHeight = browser === 'Firefox' ? '100%' : 0;

interface TableComponentProps {
  styleType: LessonComponentStyleType;
  component: TableComponent;
}

export const TableComponentEditor = (props: TableComponentProps) => {
  const muiTheme = useTheme();
  const fontFamily = muiTheme.typography.fontFamily;
  const primaryColor = muiTheme.palette.primary.main;
  const { borderColor, dataBgColor, headerBgColor } =
    props.component.settings.tableSettings;
  const header: TableRowItem = props.component.content.table.header;
  const rows: TableRowItem[] = props.component.content.table.rows;
  const [mouseHoverIndex, setMouseHoverIndex] = useState<number>(null);
  const { handleSettingsChange } = useLessonEditContext();

  const handleColumnChange = (text: string, id: string, cellIndex: number) => {
    const copyComp = cloneDeep(props.component);
    copyComp.content.table.header.cells[cellIndex] = { id, text };
    handleSettingsChange(copyComp);
  };

  const handleRowChange = (
    text: string,
    rowItemIndex: number,
    cellIndex: number
  ) => {
    const copyComp = cloneDeep(props.component);
    copyComp.content.table.rows[rowItemIndex].cells[cellIndex] = { text };
    handleSettingsChange(copyComp);
  };

  const createRow = {
    id: nanoid(),
    cells: NewComponentService.generateTableCells(header.cells.length)
  };

  const handleAddRow = (rowItemIndex: number) => {
    const copyComp = cloneDeep(props.component);
    copyComp.content.table.rows.splice(rowItemIndex, 0, createRow);
    handleSettingsChange(copyComp);
  };

  const handleDeleteRow = (rowItemIndex: number) => {
    const copyRows = cloneDeep(props.component.content.table.rows);
    copyRows.splice(rowItemIndex, 1);
    const copyComp = cloneDeep(props.component);
    if (copyRows.length > 0) {
      copyComp.content.table.rows = copyRows;
    } else {
      copyComp.content.table.rows = [createRow];
    }
    handleSettingsChange(copyComp);
  };

  const handleAddColumn = (columnItemIndex: number) => {
    const copyComp = cloneDeep(props.component);
    copyComp.content.table.header.cells.splice(columnItemIndex, 0, {
      id: nanoid(),
      text: ''
    });

    copyComp.content.table.rows.map((row) => {
      row.cells.splice(columnItemIndex, 0, { text: '' });
    });
    handleSettingsChange(copyComp);
  };

  const handleDeleteColumn = (columnItemIndex: number) => {
    const copyComp = cloneDeep(props.component);
    copyComp.content.table.header.cells.splice(columnItemIndex, 1);
    copyComp.content.table.rows.map((row) => {
      row.cells.splice(columnItemIndex, 1);
    });

    if (copyComp.content.table.header.cells.length < 1) {
      const defaultTableComponent = NewComponentService.generateTableComponent(
        props.component.type
      );
      copyComp.content = defaultTableComponent.content;
      copyComp.settings = defaultTableComponent.settings;
      copyComp.style = defaultTableComponent.style;
    }
    setMouseHoverIndex(undefined);
    handleSettingsChange(copyComp);
  };

  const handleRowDragEnd = (result: DropResult) => {
    // prevent from destination index outbound error
    if (!result.destination) return;
    const fromIndex = result.source.index;
    const toIndex = result.destination.index;
    const copyRows = cloneDeep(rows);
    const movingItem = copyRows[fromIndex];
    copyRows.splice(fromIndex, 1);
    copyRows.splice(toIndex, 0, movingItem);
    const copy = cloneDeep(props.component);
    copy.content.table.rows = copyRows;
    handleSettingsChange(copy);
  };

  const getDroppableStyle = (isDraggingOver: boolean) => ({
    background: isDraggingOver ? '#eee' : 'transparent',
    width: '100%',
    height: '100%'
  });

  const handleColumnDragEnd = (result: DropResult) => {
    // prevent from destination index outbound error
    if (!result.destination) return;
    const fromIndex = result.source.index;
    const toIndex = result.destination.index;
    const copyColumns = cloneDeep(header);
    const movingItem = copyColumns.cells[fromIndex];
    copyColumns.cells.splice(fromIndex, 1);
    copyColumns.cells.splice(toIndex, 0, movingItem);
    const copy = cloneDeep(props.component);
    copy.content.table.header = copyColumns;

    const copyRows = cloneDeep(rows);
    copyRows.map((row) => {
      const movingItem = row.cells[fromIndex];
      row.cells.splice(fromIndex, 1);
      row.cells.splice(toIndex, 0, movingItem);
    });
    copy.content.table.rows = copyRows;
    handleSettingsChange(copy);
  };

  return (
    <TableContainer className={styles.tableEditContainer}>
      <Table className="table">
        <DragDropContext onDragEnd={handleColumnDragEnd}>
          <Droppable droppableId="columns" direction="horizontal">
            {(provided1, snapshot1) => (
              <TableHead
                ref={provided1.innerRef}
                style={getDroppableStyle(snapshot1.isDraggingOver)}
                className="tableEditHeader"
              >
                <TableRow
                  style={{
                    overflow: 'auto',
                    verticalAlign: 'top'
                  }}
                >
                  {header.cells.map((cell, index) => (
                    <Draggable
                      key={cell.id}
                      draggableId={cell.id}
                      index={index}
                    >
                      {(provided2) => (
                        <TableCell
                          sx={{
                            padding: 0,
                            minWidth: '75px',
                            height: cellHeight,
                            ...LessonComponentService.tableRowBorderStyle(
                              borderColor,
                              index,
                              mouseHoverIndex === index
                                ? ColorPickerService.hexToRgbA(
                                    ColorPickerService.changeOpacity(
                                      primaryColor,
                                      0.15
                                    )
                                  )
                                : headerBgColor,
                              true
                            )
                          }}
                          ref={provided2.innerRef}
                          {...provided2.draggableProps}
                        >
                          <Grid
                            container
                            direction="column"
                            style={{ height: '100%' }}
                          >
                            <Grid
                              container
                              className={styles.deleteColumnContainer}
                              onMouseEnter={() => setMouseHoverIndex(index)}
                              onMouseLeave={() => setMouseHoverIndex(null)}
                              onClick={() => handleDeleteColumn(index)}
                            >
                              <Grid
                                className={styles.deleteColumnButtonContainer}
                              >
                                <IconButton
                                  size="small"
                                  color="primary"
                                  className={styles.deleteColumnButton}
                                >
                                  <RemoveCircleOutlinedIcon />
                                </IconButton>
                              </Grid>
                              <Grid
                                style={{
                                  backgroundColor: `${primaryColor}`
                                }}
                                className={styles.deleteColumnBar}
                              ></Grid>
                            </Grid>

                            <Grid
                              container
                              wrap="nowrap"
                              {...provided2.dragHandleProps}
                              style={{
                                cursor: 'grab',
                                // In order to fit this container to its parent div, detect deleteColumnContainer height / 2
                                height: 'calc(100% - 4px)'
                              }}
                            >
                              <Grid
                                className={styles.addColumnContainer}
                                onClick={() => handleAddColumn(index)}
                              >
                                <AddColumnElement />
                              </Grid>
                              <Grid
                                className={`${
                                  styles.headerCell
                                } style-p tableCell-placeholder ${
                                  index === 0 ? 'firstCell' : ''
                                }`}
                                style={{ fontFamily }}
                              >
                                <InlineQuillEdit
                                  value={cell.text}
                                  styleType={props.styleType}
                                  componentKey={props.component.componentkey}
                                  placeholder={'Heading'}
                                  onChange={(value) =>
                                    handleColumnChange(value, cell.id, index)
                                  }
                                />
                              </Grid>
                              {index === header.cells.length - 1 && (
                                <Grid
                                  className={styles.addColumnContainer}
                                  onClick={() => handleAddColumn(index + 1)}
                                >
                                  <AddColumnElement lastColumn />
                                </Grid>
                              )}
                            </Grid>
                          </Grid>
                        </TableCell>
                      )}
                    </Draggable>
                  ))}
                  {provided1.placeholder}
                </TableRow>
              </TableHead>
            )}
          </Droppable>
        </DragDropContext>
        <DragDropContext onDragEnd={handleRowDragEnd}>
          <Droppable droppableId="rows">
            {(provided1, snapshot1) => (
              <TableBody
                ref={provided1.innerRef}
                style={getDroppableStyle(snapshot1.isDraggingOver)}
              >
                {rows.map((row, rowItemIndex) => (
                  <Draggable
                    key={row.id}
                    draggableId={row.id}
                    index={rowItemIndex}
                  >
                    {(provided2) => (
                      <>
                        <AddRowElement
                          onClick={() => handleAddRow(rowItemIndex)}
                          row={row}
                        />
                        <TableRow
                          ref={provided2.innerRef}
                          {...provided2.draggableProps}
                        >
                          {row.cells.map((cell, cellIndex) => (
                            <TableCell
                              key={cellIndex}
                              style={{
                                ...LessonComponentService.tableRowBorderStyle(
                                  borderColor,
                                  cellIndex
                                ),
                                fontFamily,
                                minWidth: '75px',
                                height: cellHeight,
                                backgroundColor:
                                  mouseHoverIndex === cellIndex
                                    ? ColorPickerService.changeOpacity(
                                        primaryColor,
                                        0.15
                                      )
                                    : getColorString(dataBgColor)
                              }}
                              className={`style-p tableCell-placeholder ${
                                cellIndex === 0 ? 'firstCell' : ''
                              }`}
                            >
                              <InlineQuillEdit
                                value={cell.text}
                                styleType={props.styleType}
                                componentKey={props.component.componentkey}
                                placeholder={'Data'}
                                onChange={(value) =>
                                  handleRowChange(
                                    value,
                                    rowItemIndex,
                                    cellIndex
                                  )
                                }
                              />
                            </TableCell>
                          ))}
                          <TableCell
                            {...provided2.dragHandleProps}
                            style={{
                              border: 0,
                              padding: '3px 0px 0px 0px',
                              color: 'rgba(0,0,0,0.54)',
                              width: '24px'
                            }}
                          >
                            <DragIndicatorIcon />
                          </TableCell>
                          <TableCell
                            style={{ border: 0, padding: 0, width: '30px' }}
                          >
                            <IconButtonWithConfirmation
                              size="small"
                              onConfirm={() => handleDeleteRow(rowItemIndex)}
                            >
                              <DeleteIcon />
                            </IconButtonWithConfirmation>
                          </TableCell>
                        </TableRow>
                        {rowItemIndex === rows.length - 1 && (
                          <AddRowElement
                            key={rowItemIndex}
                            onClick={() => handleAddRow(rowItemIndex + 1)}
                            row={row}
                          />
                        )}
                      </>
                    )}
                  </Draggable>
                ))}
                {provided1.placeholder}
              </TableBody>
            )}
          </Droppable>
        </DragDropContext>
      </Table>
    </TableContainer>
  );
};
